import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, RefreshCcw, XCircle } from "lucide-react";

/**
 * Informed OS Academy — AssessmentResultPanel (Phase 1d).
 *
 * Renders the submit response respecting the snapshotted feedback policy.
 * The server has already stripped disallowed feedback fields; this
 * component just renders what it receives without re-checking the policy
 * against a local table (defense in depth against client-side leak).
 */
export default function AssessmentResultPanel({ result, onRetake, backHref }) {
  const attempt = result.attempt || {};
  const feedback = result.feedback || { policy: "score_only" };
  const passed = Boolean(attempt.passed);
  const scorePct = Math.round(attempt.score_pct || 0);

  return (
    <div
      data-testid="academy-assessment-result"
      className="rounded-xl border border-v2-border bg-v2-surface p-8"
    >
      <div className="flex items-center gap-3">
        {passed ? (
          <CheckCircle2 data-testid="academy-assessment-result-pass-icon" className="w-8 h-8 text-[var(--v2-tier-ready)]" />
        ) : (
          <XCircle data-testid="academy-assessment-result-fail-icon" className="w-8 h-8 text-[var(--v2-danger)]" />
        )}
        <div>
          <p
            className="text-[24px] font-semibold text-v2-text"
            style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
            data-testid="academy-assessment-result-title"
          >
            {passed ? "You passed" : "Not passed yet"}
          </p>
          {feedback.policy !== "none" ? (
            <p data-testid="academy-assessment-result-score" className="text-v2-text-muted mt-1">
              Score: <strong className="text-v2-text">{scorePct}%</strong>
              {attempt.total_points_earned != null && attempt.total_points_possible != null ? (
                <span className="ml-2">
                  ({attempt.total_points_earned}/{attempt.total_points_possible} points)
                </span>
              ) : null}
            </p>
          ) : null}
        </div>
      </div>

      {feedback.policy === "per_q" || feedback.policy === "full" ? (
        <ul data-testid="academy-assessment-result-breakdown" className="mt-6 space-y-2">
          {(feedback.questions || []).map((q, idx) => (
            <li
              key={q.question_id}
              data-testid={`academy-assessment-result-q-${q.question_id}`}
              className={`rounded-lg border p-3 flex items-start gap-3 ${
                q.is_correct
                  ? "border-[var(--v2-tier-ready)] bg-v2-background"
                  : "border-[var(--v2-danger)] bg-v2-background"
              }`}
            >
              {q.is_correct ? (
                <CheckCircle2 className="w-4 h-4 text-[var(--v2-tier-ready)] mt-0.5 flex-shrink-0" />
              ) : (
                <XCircle className="w-4 h-4 text-[var(--v2-danger)] mt-0.5 flex-shrink-0" />
              )}
              <div className="min-w-0">
                <p className="text-sm text-v2-text font-medium">Question {idx + 1}</p>
                {feedback.policy === "full" && q.explanation_markdown ? (
                  <p className="text-sm text-v2-text-muted mt-1 whitespace-pre-wrap">
                    {q.explanation_markdown}
                  </p>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      <div className="mt-8 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={onRetake}
          data-testid="academy-assessment-result-retake-btn"
          className="inline-flex items-center gap-2 px-5 py-3 rounded-lg bg-[var(--v2-accent)] text-white font-semibold hover:opacity-90"
        >
          <RefreshCcw className="w-4 h-4" />
          Retake
        </button>
        {backHref ? (
          <Link
            to={backHref}
            data-testid="academy-assessment-result-back-link"
            className="inline-flex items-center gap-2 px-4 py-3 rounded-lg border border-v2-border text-v2-text hover:bg-v2-surface-muted"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
        ) : null}
      </div>
    </div>
  );
}
